/**
 * Maps ContentAction → i18n labels. TEMPORARY — remove when backend owns actions.
 *
 * UI (not debt): bulk actions are rendered as ConversationBubble entries in the
 * message thread (same shell as “load more”), one bubble per AIResponse with
 * contentAction — see MessageItem. The bubble acts on that response's own inline
 * list; product chose bubbles in history instead of a prompt by the list.
 *
 * Target architecture (preconised):
 * - Backend returns the action proposal (prompt label, button label, what to run).
 * - Frontend only answers yes or no; no ContentAction mapping, no runBulkAction switch.
 * - ConversationBubble may remain as layout; copy and handlers come from the API.
 *
 * Technical debt (2026-05 palliative):
 * - This file + runBulkAction in Vistassistant: front invents labels/handlers.
 * - MessageList/MessageItem: isLastMessage ⇒ “superseded”, actionDoneRequestIds ⇒ success.
 * - Mutations run against each response's React Query list cache (keyed by
 *   userRequestId), not a backend-bound action payload.
 *
 * Backend should own: status per userRequestId, persistence after refresh, mail set.
 */
import { ContentAction } from "@/domain/entities/vistassistant/AIResponse";
import { TFunction } from "i18next";

/**
 * @param count - number of mails the action applies to, given in the question
 * ("Supprimer ces 12 mails ?", F-1.7); singular and plural come from the translations.
 */
const getVistassistantActionLabels = (
    contentAction: ContentAction,
    t: TFunction,
    count: number,
): { message: string; button: string } | null => {
    switch (contentAction) {
        case ContentAction.DELETE:
            return {
                message: t("vistassistant.actions.confirmDelete", { count }),
                button: t("emails.actions.delete"),
            };
        case ContentAction.MARKASREAD:
            return {
                message: t("vistassistant.actions.confirmMarkAsRead", {
                    count,
                }),
                button: t("emails.actions.markAsRead"),
            };
        case ContentAction.MARKASUNREAD:
            return {
                message: t("vistassistant.actions.confirmMarkAsUnread", {
                    count,
                }),
                button: t("emails.actions.markAsUnread"),
            };
        case ContentAction.MARKASFOCUS:
            return {
                message: t("vistassistant.actions.confirmMarkAsFocus", {
                    count,
                }),
                button: t("emails.actions.moveToFocus"),
            };
        case ContentAction.MARKASUNFOCUS:
            return {
                message: t("vistassistant.actions.confirmMarkAsUnfocus", {
                    count,
                }),
                button: t("emails.actions.removeFromFocus"),
            };
        case ContentAction.MARKASUSERPROCESSED:
            return {
                message: t("vistassistant.actions.confirmMarkAsProcessed", {
                    count,
                }),
                button: t("emails.actions.markAsProcessed"),
            };
        case ContentAction.MARKASUSERUNPROCESSED:
            return {
                message: t("vistassistant.actions.confirmMarkAsUnprocessed", {
                    count,
                }),
                button: t("emails.actions.markAsUnprocessed"),
            };
        default:
            return null;
    }
};

export default getVistassistantActionLabels;
